import {adminApprovalsRoutes} from '@app/admin/approvals/admin-approvals-routes';
import {adminDealersRoutes} from '@app/admin/dealers/admin-dealers-routes';
import {adminOrganizationsRoutes} from '@app/admin/organizations/admin-organizations-routes';
import {adminCustomPagesRoutes} from '@common/admin/custom-pages/admin-custom-pages-routes';
import {adminFileEntriesRoutes} from '@common/admin/file-entry/admin-file-entries-routes';
import {adminLogsRoutes} from '@common/admin/logging/admin-logs-routes';
import {adminRolesRoutes} from '@common/admin/roles/admin-roles-routes';
import {commonAdminSettingsRoutes} from '@common/admin/settings/common-admin-settings-routes';
import {adminBillingRoutes} from '@common/admin/subscriptions/admin-billing-routes';
// tags admin page is parked with the rest of the custom-tags feature - see
// details-sidebar-properties.tsx for the full re-enable checklist
// import {adminTagsRoutes} from '@common/admin/tags/admin-tags-routes';
import {adminLocalizationsRoutes} from '@common/admin/translations/admin-localizations-routes';
import {adminUsersRoutes} from '@common/admin/users/admin-users-routes';
import {useAuth} from '@common/auth/use-auth';
import {authGuard} from '@common/auth/guards/auth-route';
import {Navigate, RouteObject, useLocation} from 'react-router';

/**
 * Where /admin lands you.
 *
 * Reports for staff, but a dealer holds no reports.view, so forwarding
 * everyone there answered a dealer's only route into the admin area with an
 * error page. Organizations is the customer list a dealer came to manage, and
 * every dealer holds organizations.view.
 */
function AdminHome() {
  const {hasPermission} = useAuth();
  return (
    <Navigate
      to={hasPermission('reports.view') ? 'reports' : 'organizations'}
      replace
    />
  );
}

/**
 * The License report used to live under the British spelling, so bookmarks
 * made before the rename still open it, with their filters kept.
 */
function OldLicenseReportAddress() {
  const {search} = useLocation();
  return <Navigate to={`../licenses${search}`} replace />;
}

function OldApiPermissionsAddress() {
  const {search, hash} = useLocation();
  return (
    <Navigate to={`/admin/roles/api-permissions${search}${hash}`} replace />
  );
}

export const adminRoutes: RouteObject[] = [
  {
    path: 'admin',
    loader: () => authGuard({permission: 'admin.access'}),
    lazy: () => import('@common/admin/admin-layout'),
    children: [
      {
        index: true,
        element: <AdminHome />,
      },
      {
        // which parts of the REST API each role may use. Its own permission
        // rather than roles.update: reshaping roles stays super-admin-only,
        // because a role holder who could edit permissions would grant
        // themselves the admin wildcard - capability flags cannot, so a
        // Developer holds this one (see ApiCapabilities)
        path: 'roles/api-permissions',
        loader: () => authGuard({permission: 'api_permissions.update'}),
        lazy: () => import('@app/admin/api-permissions/api-permissions-page'),
      },
      {
        path: 'api-permissions',
        loader: () => authGuard({permission: 'api_permissions.update'}),
        element: <OldApiPermissionsAddress />,
      },
      {
        // all customers' applied license/add-on changes; same permission as
        // the customer list, so dealers can open it (scoped server-side)
        path: 'history',
        loader: () => authGuard({permission: 'organizations.view'}),
        lazy: () => import('@app/admin/history/organization-history-page'),
      },
      {
        path: 'reports',
        // reports are platform-wide, so they need their own gate rather than
        // riding on the layout's admin.access - dealer staff hold that. The
        // sidebar item was gated already, but the route itself was not, so the
        // page opened for anyone who arrived by URL. The Admin Area menu item
        // used to point straight here, which walked every dealer into it.
        // The parent loader runs for every child, so one gate covers all.
        loader: () => authGuard({permission: 'reports.view'}),
        children: [
          {
            index: true,
            lazy: () => import('@app/admin/reports/reports-landing-page'),
          },
          {
            path: 'licenses',
            lazy: () => import('@app/admin/reports/license-report-page'),
          },
          {
            // the old British spelling, kept on purpose for old bookmarks
            path: 'licences',
            element: <OldLicenseReportAddress />,
          },
          {
            path: 'storage',
            lazy: () => import('@app/admin/reports/storage-report-page'),
          },
          {
            path: 'api-usage',
            lazy: () => import('@app/admin/reports/api-usage-report-page'),
          },
          {
            // one customer opened up. The parent loader above gates this too,
            // and the server independently resolves the customer through
            // reportOrganizations() - ids are sequential, so the page must not
            // be the only thing standing between a URL and another tenant.
            path: 'api-usage/:organizationId',
            lazy: () => import('@app/admin/reports/api-usage-detail-page'),
          },
        ],
      },
      ...Object.values(adminUsersRoutes),
      ...Object.values(adminOrganizationsRoutes),
      ...Object.values(adminDealersRoutes),
      ...Object.values(adminApprovalsRoutes),
      ...Object.values(adminRolesRoutes),
      ...Object.values(adminBillingRoutes),
      ...Object.values(adminCustomPagesRoutes),
      // ...Object.values(adminTagsRoutes),
      ...Object.values(adminLocalizationsRoutes),
      ...Object.values(adminFileEntriesRoutes),
      ...Object.values(adminLogsRoutes),

      commonAdminSettingsRoutes(
        [
          {
            path: 'drive',
            lazy: () => import('@app/admin/settings/drive-settings'),
          },
          {
            path: 'landing-page',
            lazy: () =>
              import(
                '@common/admin/settings/landing-page-settings/landing-page-settings'
              ),
          },
          {
            path: 'announcements',
            lazy: () => import('@app/admin/settings/announcement-settings'),
          },
          {
            path: 'acknowledgment',
            lazy: () => import('@app/admin/settings/acknowledgment-settings'),
          },
          {
            path: 'license-portal',
            lazy: () => import('@app/admin/settings/license-portal-settings'),
          },
          {
            path: 'sip-notify',
            lazy: () => import('@app/admin/settings/sip-notify-settings'),
          },
          {
            path: 'approvals',
            lazy: () => import('@app/admin/settings/approvals-settings'),
          },
        ],
        {
          uploading: {
            lazy: () =>
              import(
                '@common/admin/settings/pages/uploading-settings/uploading-settings'
              ),
          },
          general: {
            lazy: () => import('@app/admin/settings/app-general-settings'),
          },
        },
      ),
    ],
  },
];
